// The rubric, wherever a score is (media-ux §4): five levels with the
// achieved one lit. Every grade stores the rubric it was judged under
// (rubric_used) and every mapped skill carries rubric_json — this component
// just finally shows them. Falls back to the standard developmental rubric
// (the same ladder B2a grades against when a skill has no rubric of its own)
// and says which one it is.
import { ScoreChip } from '../ds';
import { cn } from '../../lib/cn';

const DEFAULT_LEVELS = [
  { score: 1, desc: 'Unable to demonstrate the skill at this time' },
  { score: 2, desc: 'Skill is emerging with consistent adult support' },
  { score: 3, desc: 'Skill is developing well with occasional adult support' },
  { score: 4, desc: 'Skill is developed and independently applied' },
  { score: 5, desc: 'Skill is fully developed and applied with innovation and creativity' },
];

// The achieved level's own words — the one-line verdict a score chip wears
// wherever it appears (GradeDetailModal's verdict row). Exported so the
// modal and the ladder can never disagree about what a 3 means.
export function levelDescFor(rubric, score) {
  if (score == null) return null;
  const levels = rubric && Array.isArray(rubric.levels) ? rubric.levels : DEFAULT_LEVELS;
  const hit = levels.find((l) => Number(l.score) === Number(score));
  return (hit && (hit.desc || hit.label)) || DEFAULT_LEVELS.find((d) => d.score === Number(score))?.desc || null;
}

function levelsOf(rubric) {
  const levels = rubric && Array.isArray(rubric.levels) ? rubric.levels : null;
  if (!levels || levels.length === 0) return { levels: DEFAULT_LEVELS, custom: false };
  const byScore = new Map(levels.map((l) => [Number(l.score), l]));
  return {
    levels: DEFAULT_LEVELS.map((d) => {
      const l = byScore.get(d.score);
      return { score: d.score, desc: (l && (l.desc || l.label)) || d.desc };
    }),
    custom: true,
  };
}

export default function RubricLadder({ rubric, score }) {
  const { levels, custom } = levelsOf(rubric);
  const achieved = score == null ? null : Number(score);

  return (
    <div className="flex flex-col gap-1">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {custom ? 'Rubric for this skill' : 'Standard developmental rubric'}
      </div>
      {levels.map((l) => (
        <div
          key={l.score}
          className={cn(
            'flex items-start gap-2.5 rounded-lg border px-2.5 py-1.5 text-[12px] leading-snug',
            achieved === l.score
              ? 'border-success/60 bg-success/10 text-foreground'
              : 'border-border bg-card text-muted-foreground'
          )}
        >
          <ScoreChip score={l.score} />
          <span className={cn('pt-0.5', achieved === l.score && 'font-medium')}>
            {l.desc}
            {l.score === 4 && (
              <span className="ml-1 text-[10.5px] font-semibold text-success">
                · proficient at grade
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}